import { Button, DialogActions } from '@mui/material'
import { FC, useCallback, useContext, useEffect, useMemo } from 'react'

import { sdk } from '../../../config'
import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { useGetShopCannedJobExpanded } from '../../../hooks/useGetShopCannedJobExpanded'
import { RepairOrderContext } from '../../../providers/RepairOrderProvider/RepairOrderProvider'
import { SubmitDialogContext } from '../../../providers/SubmitDialogProvider/SubmitDialogProvider'
import { ServiceExpanded } from '../../../sdk'
import { sendBugReport } from '../../../utils/handleError/sendBugReport'
import { CenteredLoading } from '../../CenteredLoading/CenteredLoading'
import CannedJobSelectionInfoPanel from '../../SharedUI/CustomerForms/CannedJobSelectionInfoPanel'
import { AddServiceDialogContentWrapper } from './AddServiceDialogContentWrapper'
import { SearchServiceProps } from './LookupServiceWindow'
import { SearchAll } from './SearchAll'
import { SearchCannedJob } from './SearchCannedJob'
import {
  getSelectedMotorFluidsFromCannedJob,
  getSelectedMotorJobFromCannedJob,
  getSelectedPartLookup,
  INITIAL_SELECTED,
  ModifiedCannedJobExpanded,
  SelectedJob,
  selectedJobIsEmpty
} from './transformSelectedJob'
import { useSubmitCannedJobOrMotor } from './useSubmitCannedJobOrMotor'
const getRepairOrderServices = sdk.getRepairOrderServices.bind(sdk)
export function hasMotorSelection(
  cannedJob: ModifiedCannedJobExpanded
): boolean {
  return (
    getSelectedMotorJobFromCannedJob(cannedJob).length > 0 ||
    getSelectedMotorFluidsFromCannedJob(cannedJob).length > 0
  )
}
export function hasPartsTechSelection(
  cannedJob: ModifiedCannedJobExpanded
): boolean {
  return getSelectedPartLookup(cannedJob).length > 0
}

export type ServiceIdToCannedJob = {
  [key: string]: ModifiedCannedJobExpanded
}

export const SearchCannedJobOrMotor: FC<
  SearchServiceProps & {
    cannedJobOnly: boolean
    setStepperProps: (
      p:
        | {
            services: ServiceExpanded[]
            cannedJob: ModifiedCannedJobExpanded[]
          }
        | undefined
    ) => void
  }
> = ({
  cannedJobOnly,
  submitCallback,
  cancelCallback,
  selected,
  setSelected,
  setStepperProps
}) => {
  const { submitCannedJobAndMotor } = useSubmitCannedJobOrMotor()
  const { setIsSubmitting } = useContext(SubmitDialogContext)
  const { customer, vehicle, repairOrder } = useContext(RepairOrderContext)
  const { accessToken, shopId } = useAccessTokenAndShopId()

  const { cannedJobsExpanded, isLoading: loadingCannedJobs } =
    useGetShopCannedJobExpanded()
  const hasMotorLabor = useMemo(
    () =>
      selected.canned.some(
        (c) => getSelectedMotorJobFromCannedJob(c).length !== 0
      ),
    [selected.canned]
  )
  const hasMotorFluids = useMemo(
    () =>
      selected.canned.some(
        (c) => getSelectedMotorFluidsFromCannedJob(c).length !== 0
      ),
    [selected.canned]
  )
  const hasPartsTech = useMemo(
    () => selected.canned.some((c) => getSelectedPartLookup(c).length !== 0),
    [selected.canned]
  )
  const handleSubmit = useCallback(
    async (selected: SelectedJob, skip: boolean) => {
      if (selectedJobIsEmpty(selected)) {
        return
      }

      setIsSubmitting(true)
      const serviceIds = await submitCannedJobAndMotor(selected)

      if (!skip) {
        let services: ServiceExpanded[] = []
        let foundServices = false

        for (let i = 0; i < 5; i++) {
          services = await getRepairOrderServices({
            authorization: accessToken,
            shop: shopId,
            repairOrder: [repairOrder!.entry!.id!]
          })
          foundServices = serviceIds.every((id) =>
            services.map((s) => s.entry?.id).includes(id)
          )
          if (foundServices) {
            // No need to continue the loop if we found the services
            break
          } else {
            // If services are not found, wait for 500ms before next try
            await new Promise((resolve) => setTimeout(resolve, 500))
          }
        }
        if (foundServices) {
          setStepperProps({
            services: services.filter((s) => serviceIds.includes(s.entry!.id!)),
            cannedJob: selected.canned
          })
        } else {
          cancelCallback()
          sendBugReport(
            'error',
            'Cannot find services created',
            'Cannot find services created'
          )
        }
        setIsSubmitting(false)
      } else {
        await submitCallback(serviceIds)
        setIsSubmitting(false)
      }
    },
    [
      setIsSubmitting,
      submitCannedJobAndMotor,
      accessToken,
      shopId,
      repairOrder,
      setStepperProps,
      cancelCallback,
      submitCallback
    ]
  )

  useEffect(() => {
    setSelected(INITIAL_SELECTED)
  }, [cannedJobOnly, setSelected])

  return (
    <>
      <AddServiceDialogContentWrapper>
        {vehicle && customer && (
          <CannedJobSelectionInfoPanel
            repairOrderId={repairOrder!.entry!.id!}
            selected={selected}
            setSelected={setSelected}
            cannedJobsExpanded={cannedJobsExpanded}
          />
        )}
        {cannedJobOnly && (
          <SearchCannedJob
            cannedJobsExpanded={cannedJobsExpanded || []}
            selected={selected}
            setSelected={setSelected}
          />
        )}
        {!cannedJobOnly && (
          <SearchAll
            selected={selected}
            setSelected={setSelected}
            cannedJobsExpanded={cannedJobsExpanded || []}
          />
        )}
        {cannedJobsExpanded.length === 0 && loadingCannedJobs && (
          <CenteredLoading />
        )}
      </AddServiceDialogContentWrapper>
      <DialogActions>
        <Button onClick={() => cancelCallback()}>Cancel</Button>
        {hasMotorLabor && (
          <Button
            variant="contained"
            type="submit"
            onClick={handleSubmit.bind(null, selected, false)}
            disabled={selectedJobIsEmpty(selected)}>
            Save and Select Labor
          </Button>
        )}
        {!hasMotorLabor && (hasMotorFluids || hasPartsTech) && (
          <Button
            variant="contained"
            type="submit"
            onClick={handleSubmit.bind(null, selected, false)}
            disabled={selectedJobIsEmpty(selected)}>
            Save and Source Parts
          </Button>
        )}
        <Button
          variant="contained"
          type="submit"
          onClick={handleSubmit.bind(null, selected, true)}
          disabled={selectedJobIsEmpty(selected)}>
          Save
        </Button>
      </DialogActions>
    </>
  )
}
